import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { updateAppointment, updateAppointmentStatusManual } from "../../store/thunks/appointmentsThunk";

export default function EditStatusModal({ isOpen, onClose, appointment }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  
  // Define available statuses based on the schema
  const availableStatuses = [
    "Booked",
    "Changed",
    "Cancelled",
    "Cancelled - No Fee",
    "Cancelled - Charge Fee",
    "Cancelled - Clinician",
    "No Show",
    "No Show - No Fee",
    "No Show - Charge Fee",
    "Tentative",
    "Confirmed",
    "Treated",
    "Invoiced",
    "Invoice Paid",
    "Reminder Email Sent",
    "Invoice Email Sent",
    "SMS Reminder Sent",
    "Soap Notes Saved",
    "Soap Notes Completed",
    "Soap Notes Updated",
    "Soap Notes Approved",
    "Comments",
    "Approved for Payroll",
    "Paid to Clinician",
    "Completed",
    "Carryover"
  ];

  useEffect(() => {
    if (appointment && appointment.status) {
      // Filter out any invalid statuses that aren't in the availableStatuses list
      const validStatuses = appointment.status.filter(status => 
        availableStatuses.includes(status)
      );
      setSelectedStatuses(validStatuses);
    }
  }, [appointment]);

  const handleStatusToggle = (status) => {
    if (selectedStatuses.includes(status)) {
      setSelectedStatuses(selectedStatuses.filter((s) => s !== status));
    } else {
      setSelectedStatuses([...selectedStatuses, status]);
    }
  };

  const handleSave = async () => {
    if (!appointment) return;

    try {
      // Check if user is an owner
      const isOwner = user?.role?.includes("owner");
      
      if (isOwner) {
        // For owners, use the manual status update that doesn't modify history
        await dispatch(updateAppointmentStatusManual({ 
          appointmentId: appointment._id, 
          status: selectedStatuses,
          userId: user._id
        })).unwrap();
      } else {
        // For non-owners, use the regular update that includes history
        const historyItem = {
          status: "Changed",
          timestamp: new Date(),
          comments: `Status updated to: ${selectedStatuses.join(", ")}`,
          user: user._id
        };

        const updatedData = {
          status: selectedStatuses,
          history: [...(appointment.history || []), historyItem]
        };

        await dispatch(updateAppointment({ 
          id: appointment._id, 
          updatedData 
        })).unwrap();
      }
      
      onClose();
    } catch (error) {
      console.error("Failed to update appointment status:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-2xl bg-white rounded-sm shadow-md relative mt-8">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 transition-colors">
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="p-4 overflow-y-auto max-h-[70vh] flex flex-col">
          <h2 className="text-base font-semibold text-gray-900 mb-2">Edit Appointment Status</h2>
          <p className="text-xs text-gray-600 mb-3">
            Select the statuses that apply to this appointment. You can select multiple statuses.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 mb-4">
            {availableStatuses.map((status) => (
              <div
                key={status}
                className={`p-1.5 rounded-sm cursor-pointer transition-all ${
                  selectedStatuses.includes(status)
                    ? "bg-indigo-50 border border-indigo-200 shadow-sm"
                    : "bg-transparent border border-gray-100 hover:bg-gray-50/80"
                }`}
                onClick={() => handleStatusToggle(status)}>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedStatuses.includes(status)}
                    onChange={() => {}}
                    className="h-3.5 w-3.5 text-indigo-600 focus:ring-indigo-500 border-gray-200 rounded-sm mr-2"
                  />
                  <span className="text-xs text-gray-700">{status}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-sm transition-colors">
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-sm transition-colors shadow-sm">
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 